/**
 * Emergency Recover Page
 *
 * Public route (no Developer Mode gate) — a non-technical contractor can use
 * this page to back up unsynced work from their device when the offline
 * pipeline gets stuck.
 *
 * Layout strategy:
 *   • Functional actions (Download backup, Force sync) are ALWAYS visible —
 *     these are what the user came here to do.
 *   • Friendly per-visit info (client name, address, scheduled date) is
 *     ALWAYS visible — that's how the user identifies the right visit.
 *   • Technical details (visit UUIDs, raw store names, byte counts, queue
 *     diagnostics) ONLY appear when Developer Mode is enabled for the org.
 *
 * Designed for the SolidHome incident (Feb 2026): contractor on an iPad
 * hotspot with no Mac for devtools, panicking that data is lost. Needs a
 * one-tap rescue, not a debugger.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Download, AlertTriangle, Loader2, Image as ImageIcon, FileText, RefreshCw, ArrowLeft, MapPin, Calendar, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useOffline } from '@/context/OfflineContext';
import { useDeveloperMode } from '@/hooks/useDeveloperMode';
import { STORES, getAll } from '@/lib/offlineDB';
import { dumpQueueDiagnostics } from '@/lib/offlineQueue';
import { apiHelpers } from '@/lib/api';

function formatVisitDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return null;
  }
}

export default function EmergencyRecoverPage() {
  const { user, organization } = useAuth();
  const { syncPendingActions, isSyncing } = useOffline();
  const developerMode = useDeveloperMode();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [drafts, photos, queue] = await Promise.all([
        getAll(STORES.FIELD_NOTES_DRAFTS),
        getAll(STORES.PHOTO_UPLOADS),
        getAll(STORES.OFFLINE_QUEUE),
      ]);

      // Group by site_visit_id
      const byVisit = new Map();
      const ensure = (visitId) => {
        if (!byVisit.has(visitId)) {
          byVisit.set(visitId, {
            siteVisitId: visitId,
            draft: null,
            photos: [],
            queueItems: [],
            visitMeta: null, // hydrated lazily below
          });
        }
        return byVisit.get(visitId);
      };

      drafts.forEach((d) => {
        if (!d.site_visit_id) return;
        ensure(d.site_visit_id).draft = d;
      });
      photos.forEach((p) => {
        if (!p.site_visit_id) return;
        ensure(p.site_visit_id).photos.push(p);
      });
      queue.forEach((q) => {
        const visitId = q.payload?.site_visit_id;
        if (!visitId) return;
        ensure(visitId).queueItems.push(q);
      });

      // Fetch friendly metadata (client name, address, scheduled date) for
      // each visit so the page reads like "Alfredo Rodriguez · 105 Sunset
      // Point Dr · Jun 14 3:00 PM" instead of an uninterpretable UUID. Best
      // effort — if the server is unreachable we just fall through.
      const visits = Array.from(byVisit.values());
      await Promise.all(visits.map(async (v) => {
        if (!organization?.id) return;
        try {
          const res = await apiHelpers.getSiteVisit(organization.id, v.siteVisitId);
          v.visitMeta = res?.data || null;
        } catch {
          v.visitMeta = null;
        }
      }));

      const list = visits
        .map((v) => ({
          ...v,
          totalPhotoBytes: v.photos.reduce((sum, p) => sum + (p.data_url?.length || 0), 0),
          photosWaiting: v.photos.filter((p) => p.status !== 'done').length,
          unsynced: v.photos.some((p) => p.status !== 'done') || v.queueItems.length > 0,
        }))
        .sort((a, b) => Number(b.unsynced) - Number(a.unsynced));

      setItems(list);
      setMeta({
        totalDrafts: drafts.length,
        totalPhotos: photos.length,
        totalQueueItems: queue.length,
      });
    } catch (e) {
      console.error('[EmergencyRecover] load failed', e);
      toast.error('Could not read local storage', { description: e.message || 'IndexedDB error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const downloadDump = async (siteVisitId = null) => {
    try {
      const dump = await dumpQueueDiagnostics();
      const [drafts, photos, queue] = await Promise.all([
        getAll(STORES.FIELD_NOTES_DRAFTS),
        getAll(STORES.PHOTO_UPLOADS),
        getAll(STORES.OFFLINE_QUEUE),
      ]);

      const filterFn = siteVisitId
        ? (x) => (x.site_visit_id === siteVisitId) || (x.payload?.site_visit_id === siteVisitId)
        : () => true;

      const payload = {
        schema_version: 1,
        emitted_at: new Date().toISOString(),
        org_id: organization?.id || null,
        org_name: organization?.name || null,
        user_id: user?.id || null,
        user_name: user?.name || null,
        site_visit_id: siteVisitId,
        user_agent: navigator.userAgent,
        online: navigator.onLine,
        raw: {
          field_notes_drafts: drafts.filter(filterFn),
          photo_uploads: photos.filter(filterFn),
          offline_queue: queue.filter(filterFn),
        },
        diagnostics: dump,
      };

      const json = JSON.stringify(payload, null, 2);
      const sizeKb = Math.round(json.length / 1024);

      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      const idTag = siteVisitId ? `-${siteVisitId.slice(0, 8)}` : '-all';
      a.download = `contractoros-emergency-recover${idTag}-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);

      toast.success(`Backup file downloaded (${sizeKb} KB)`, {
        description: 'Email or AirDrop this file to your admin. Do NOT delete it.',
        duration: 10000,
      });
    } catch (e) {
      console.error('[EmergencyRecover] download failed', e);
      toast.error('Could not generate backup file', { description: e.message || 'Unknown error' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6" data-testid="emergency-recover-page">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-sm text-gray-600 hover:text-gray-900 mb-3 inline-flex items-center gap-1"
          data-testid="emergency-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard
        </button>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-amber-900">Back up your work</p>
              <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                We found notes, measurements, or photos on this device that
                haven&apos;t reached the cloud yet. Tap <strong>Back up this visit</strong>
                {' '}to save them to a file. Email or AirDrop that file to your
                admin and they will finish the recovery.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-gray-900">Unsynced work on this device</h2>
            <button
              onClick={load}
              disabled={loading}
              className="text-xs text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="emergency-refresh-btn"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <>
              {/* Technical breakdown — Developer Mode only */}
              {developerMode && meta && (
                <div className="grid grid-cols-3 gap-2 mb-4 text-center" data-testid="emergency-dev-stats">
                  <div className="bg-gray-50 rounded-lg py-2 border border-dashed border-gray-200">
                    <p className="text-xl font-bold text-gray-900">{meta.totalDrafts}</p>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">drafts (dev)</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg py-2 border border-dashed border-gray-200">
                    <p className="text-xl font-bold text-gray-900">{meta.totalPhotos}</p>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">photos (dev)</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg py-2 border border-dashed border-gray-200">
                    <p className="text-xl font-bold text-gray-900">{meta.totalQueueItems}</p>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">queue (dev)</p>
                  </div>
                </div>
              )}

              {items.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8" data-testid="emergency-empty-state">
                  Nothing to back up — every visit on this device is fully synced.
                </p>
              ) : (
                <div className="space-y-3">
                  {items.map((it) => {
                    const meta = it.visitMeta || {};
                    const clientName = meta.client_name || meta.client?.name || null;
                    const address = meta.address || meta.location_address || null;
                    const scheduled = formatVisitDate(meta.scheduled_at || meta.start_at);
                    return (
                      <div
                        key={it.siteVisitId}
                        className={`border rounded-lg p-3 ${it.unsynced ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`}
                        data-testid={`emergency-visit-${it.siteVisitId.slice(0, 8)}`}
                      >
                        {/* Friendly identifier */}
                        <div className="mb-2">
                          <p className="text-sm font-semibold text-gray-900">
                            {clientName || 'Site visit'}
                          </p>
                          {address && (
                            <p className="text-xs text-gray-600 inline-flex items-center gap-1 mt-0.5">
                              <MapPin className="w-3 h-3" /> {address}
                            </p>
                          )}
                          {scheduled && (
                            <p className="text-[11px] text-gray-500 inline-flex items-center gap-1 mt-0.5">
                              <Calendar className="w-3 h-3" /> {scheduled}
                            </p>
                          )}
                          {/* Visit UUID — Developer Mode only */}
                          {developerMode && (
                            <p className="text-[10px] font-mono text-gray-400 mt-1 break-all" data-testid={`emergency-visit-id-${it.siteVisitId.slice(0, 8)}`}>
                              id {it.siteVisitId}
                            </p>
                          )}
                        </div>

                        {/* Functional summary — always visible */}
                        <div className="flex flex-wrap gap-3 text-[12px] text-gray-700 mb-3">
                          {(it.draft || it.queueItems.length > 0) && (
                            <span className="inline-flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              Notes &amp; measurements
                            </span>
                          )}
                          {it.photosWaiting > 0 && (
                            <span className="inline-flex items-center gap-1">
                              <ImageIcon className="w-3 h-3" />
                              {it.photosWaiting} photo{it.photosWaiting !== 1 ? 's' : ''}
                            </span>
                          )}
                          {/* Byte-size & raw counts — Developer Mode only */}
                          {developerMode && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 font-mono" data-testid={`emergency-dev-meta-${it.siteVisitId.slice(0, 8)}`}>
                              {it.draft ? '1 draft · ' : ''}
                              {it.queueItems.length} queue ·{' '}
                              {it.totalPhotoBytes > 0 && `${Math.round(it.totalPhotoBytes / 1024 / 1024)} MB`}
                            </span>
                          )}
                        </div>

                        <Button
                          onClick={() => downloadDump(it.siteVisitId)}
                          className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                          size="sm"
                          data-testid={`emergency-download-${it.siteVisitId.slice(0, 8)}`}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Back up this visit
                        </Button>
                      </div>
                    );
                  })}

                  {/* "Back up everything" button — Developer Mode only.
                       Non-dev users get per-visit buttons (cleaner & safer
                       — they back up only what they need). */}
                  {developerMode && items.length > 1 && (
                    <div className="pt-3 border-t border-dashed border-gray-200">
                      <Button
                        onClick={() => downloadDump(null)}
                        variant="outline"
                        className="w-full border-2 border-gray-300"
                        data-testid="emergency-download-all"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Back up ALL unsynced data (dev)
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm font-semibold text-gray-900 mb-1">Try syncing first</p>
          <p className="text-xs text-gray-500 mb-3 leading-relaxed">
            Already on stable Wi-Fi? Try a forced sync before downloading the
            backup file. If it works, your work will reach the cloud and you
            can skip the file-and-email step.
          </p>
          <Button
            onClick={() => syncPendingActions({ force: true })}
            disabled={isSyncing}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="emergency-force-sync-btn"
          >
            {isSyncing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Syncing&hellip;</>
            ) : (
              <><RefreshCw className="w-4 h-4 mr-2" /> Try syncing now</>
            )}
          </Button>
          {developerMode && (
            <p className="text-[10px] text-gray-400 mt-2 font-mono" data-testid="emergency-dev-sync-note">
              dev: force=true bypasses next_attempt_at backoff
            </p>
          )}
        </div>

        {developerMode && (
          <div className="mt-4 text-center" data-testid="emergency-dev-link">
            <Link to="/sync-queue" className="text-xs text-gray-400 hover:text-gray-600 underline">
              Open full sync queue (developer)
            </Link>
          </div>
        )}

        <p className="text-[10px] text-gray-400 text-center mt-4">
          ContractorOS · Back up unsynced work
        </p>
      </div>
    </div>
  );
}
